import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow, Icon } from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Trans } from 'react-i18next';

import { Box } from '../Box';
import { Typography } from '../Typography';
import { Input } from '../Input';
import typography from 'src/theme/base/typography';
import { Pagination } from '../Pagination';
import { LoaderOverlay } from '../LoaderOverlay/LoaderOverlay';

import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from '../Typography/types';

import colors from 'src/theme/base/colors';
import { SearchFilter } from '../ColumnFilter/ColumnFilter';
import { DataTableTestProps } from './types';
import { Button } from '../Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from '../Button/types';

export const DataTable = <T,>({
  title,
  data,
  columns,
  isLoading,
  pageInfo,
  onRefetchData,
  selectedRowCount,
  serverSide = false,
}: DataTableTestProps<T>) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  });

  const table = useReactTable({
    // Default options
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    // Pagination
    getPaginationRowModel: getPaginationRowModel(), // ignored if serverSide true to prio server side pagination
    manualPagination: serverSide,
    rowCount: serverSide ? pageInfo?.totalRowCount : undefined,
    pageCount: serverSide ? pageInfo?.totalPageCount : undefined,
    onPaginationChange: updater => {
      const { pageIndex } = typeof updater === 'function' ? updater(pagination) : updater;

      setPagination(updater);
      onRefetchData?.(pageIndex + 1, table.getState().columnFilters);
    },
    // Filtering
    getFilteredRowModel: getFilteredRowModel(), // ignored if serverSide true to prio server side filtering
    manualFiltering: serverSide,
    // State
    state: {
      pagination,
    },
  });

  const handleFilter = () => {
    setPagination(old => ({ ...old, pageIndex: 0 }));
    onRefetchData?.(1, table.getState().columnFilters);
  };

  const handleReset = () => {
    table.resetColumnFilters();
    setPagination(old => ({ ...old, pageIndex: 0 }));
    onRefetchData?.(1);
  };

  return (
    <TableContainer sx={{ position: 'relative' }}>
      <LoaderOverlay isLoading={!!isLoading} />

      <Box p={3}>
        {!!title && (
          <Typography variant="h5" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM} mb={2}>
            {title}
          </Typography>
        )}

        <Box display="flex" gap={2} alignItems="end" flexWrap="wrap">
          {table
            .getAllLeafColumns()
            .map(
              column => column.getCanFilter() && <SearchFilter key={column.id} column={column} />,
            )}
          <Box display="flex" gap={2}>
            <Button
              sx={{ marginBottom: 1 }}
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              disabled={table.getState().columnFilters.length === 0}
              onClick={handleReset}>
              <Trans>genericTable.resetFilters</Trans>
            </Button>
            {serverSide && (
              <Button
                variant={BUTTON_VARIANT.GRADIENT}
                color={BUTTON_COLOR.DARK}
                sx={{ marginBottom: 1 }}
                onClick={handleFilter}>
                <Trans>generic.search</Trans>
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Table sx={{ tableLayout: { md: 'fixed' } }}>
        <Box component="thead">
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableCell
                  key={header.id}
                  sx={{
                    textAlign: 'center',
                    color: colors.secondary.main,
                    minWidth: 150,
                    fontSize: typography.size.xxs,
                    fontWeight: typography.fontWeightBold,
                    textTransform: 'uppercase',
                  }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </Box>

        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <TableCell
                  sx={{
                    textAlign: 'center',
                    color: colors.text.main,
                    fontSize: typography.size.sm,
                  }}
                  key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        p={serverSide || table.getPageOptions().length > 1 ? 3 : 0}>
        {serverSide && (
          <Box>
            <Typography
              variant="button"
              color={TYPOGRAPHY_COLORS.SECONDARY}
              fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
              display="block"
              sx={{ whiteSpace: 'pre-line' }}>
              <Trans
                i18nKey="genericTable.pagination.entriesInfo"
                // values are not correct when server side pagination is off
                // but it's acceptable as this is only used with server side pagination
                // if serverSide is off, these values are not displayed
                values={{
                  filteredRowCount: pageInfo?.filteredRowCount,
                  totalRowCount: table.getRowCount(),
                  entriesStart:
                    table.getState().pagination.pageIndex * table.getState().pagination.pageSize +
                    1,
                  entriesEnd: Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    table.getRowCount(),
                  ),
                  currentPage: table.getState().pagination.pageIndex + 1,
                  totalPageCount: table.getPageCount(),
                }}
              />
            </Typography>

            {selectedRowCount !== undefined && (
              <Typography
                variant="button"
                color={TYPOGRAPHY_COLORS.SECONDARY}
                fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
                display="block">
                <Trans
                  i18nKey="genericTable.pagination.selectedRows"
                  values={{ selectedRowCount }}
                />
              </Typography>
            )}
          </Box>
        )}

        {table.getPageOptions().length > 1 && (
          <Pagination>
            {table.getCanPreviousPage() && (
              <Pagination item onClick={() => table.previousPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
              </Pagination>
            )}
            {table.getPageCount() > 6 ? (
              <Box width="5rem" mx={1}>
                <Input type="number" value={13} onChange={() => console.log('goToPage')} />
              </Box>
            ) : (
              table.getPageOptions().map((option: number) => (
                <Pagination
                  item
                  key={option}
                  onClick={() => table.setPageIndex(option)}
                  active={table.getState().pagination.pageIndex === option}>
                  {option + 1}
                </Pagination>
              ))
            )}
            {table.getCanNextPage() && (
              <Pagination item onClick={() => table.nextPage()}>
                <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
              </Pagination>
            )}
          </Pagination>
        )}
      </Box>
    </TableContainer>
  );
};
